import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteCookie, getCookie, setCookie } from "../utils/cookies";
import { getUserDetails, userLogin } from "../apis/user";
import { LoginData, User } from "../utils/types";
import { isPublicBookingHost } from "../utils/publicHost";

interface UserDataContextInterface {
  handleLogin: ({ email, password }: LoginData, event: any) => Promise<void>;
  userID: string | undefined;
  handleLogout: () => Promise<any>;
  userData: User | undefined;
  setUserData: React.Dispatch<React.SetStateAction<User | undefined>>;
  isLoggingIn: boolean;
  handleSultanLogout: () => Promise<any>;
}

export const UserDataContext = createContext({} as UserDataContextInterface);

type UserDataProviderProps = {
  children?: React.ReactNode;
};

const UserContext = ({ children }: UserDataProviderProps) => {
  const navigate = useNavigate();
  const accessToken = getCookie("accessToken");
  const userID = getCookie("userID");

  const [userData, setUserData] = useState<User>();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    const onAuthStateChanged = async () => {
      if (isPublicBookingHost()) {
        return;
      }
      if(window.location.pathname === "/sultan/login"){
        return
      }
      else if (!accessToken || !userID) {
        console.log("Cookies not present, logged out.");
        deleteCookie("accessToken");
        deleteCookie("userID");
        navigate("/");
      } else if (accessToken && userID) {
        // Token refresh is handled automatically by the apiClient interceptor (fetch.ts).
        // No need to manually refresh here — if the token is expired, the interceptor
        // will refresh it and retry the request before returning.
        const user_data = await getUserDetails(userID);

        if (user_data?.status === 200) {
          setUserData(user_data.data.result);
        } else {
          console.error("Failed to fetch user details, status:", user_data?.status);
        }
      } else console.error("User ID invalid or not present.");
    };

    onAuthStateChanged();
  }, [accessToken, userID, navigate]);

  const handleLogin = async ({ email, password }: LoginData, event: any) => {
    event.preventDefault();

    setIsLoggingIn(true)

    if (!email) {
      toast.error("Invalid email.");
    } else if (password.length <= 7) {
      toast.error("Password needs to have more than 8 characters.");
    } else {
      const res = await userLogin({ email, password });
      if (res?.status === 200) {
        const loginData = res.data.result;
        toast.success("Sign in successful!");
        
        setCookie("accessToken", loginData.access_token, 1);
        setCookie("bookingOverview", "", 30);
        setCookie("dailyOverview", "", 30);
        setCookie("userID", loginData.user_id, 30);

        const user_data = await getUserDetails(loginData.user_id);
        if (user_data?.status === 200) {
          window.location.pathname="/";
          setUserData(user_data.data.result);
        } else {
          window.location.pathname="/";
        }
      } else if (res?.status === 401) {
        toast.error("Wrong email/password entered.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
    setIsLoggingIn(false)
  };

  

  const handleLogout = async () => {
    deleteCookie("accessToken");
    deleteCookie("userID");
    deleteCookie("bookingOverview");
    deleteCookie("dailyOverview");
    deleteCookie("avgWaitTimeStats")
    setUserData(undefined);
    localStorage.clear();
    navigate("/");
  };

  const handleSultanLogout = async () => {
    deleteCookie("accessToken");
    deleteCookie("userID");
    deleteCookie("bookingOverview");
    deleteCookie("dailyOverview");
    deleteCookie("avgWaitTimeStats")
    setUserData(undefined);
    localStorage.clear();

    window.location.pathname="/sultan/login";
  };

  return (
    <UserDataContext.Provider
      value={{
        handleLogin,
        userID,
        userData,
        setUserData,
        handleLogout,
        isLoggingIn,
        handleSultanLogout
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

export function useUserData() {
  return useContext(UserDataContext);
}

export default UserContext;
